import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { useSnackbar } from "notistack";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { Button } from "../ui/forms/Button";
import { UserContext } from "../../contexts/UserContext";
import { DatabaseContext } from "../../contexts/DatabaseContext";
import Axios from "axios";
import { useFormik } from "formik";
import { PatronContext } from "../../contexts/PatronContext";
import InputLabel from "@material-ui/core/InputLabel";
import { Multiselect } from "multiselect-react-dropdown";
import { Checkbox } from "../ui/forms/Checkbox";

const INIT_USER = {
  userRole: "user",
  siteCode: "",
  telephone: "",
  position: "",
  displayName: "",
  email: "",
  password: "",
  confirmPassword: "",
  showCampaigns: true,
  sites: [],
};

const UserForm = ({ user, close, refresh }) => {
  const [currentUser, setCurrentUser] = useState(user ?? INIT_USER);
  const [currentSites, setCurrentSites] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteCodes] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const { getAllSites, createUser, updateUser } = useContext(UserContext);
  const { apiUrl } = useContext(DatabaseContext);
  const [submitVisible, setSubmitVisible] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (currentUser.password !== currentUser.confirmPassword) {
      enqueueSnackbar(`Passwords don't match`, { variant: "error" });
      return;
    }
    try {
      if (!user) {
        await createUser({ ...currentUser, sites: currentSites });
      } else {
        await updateUser(currentUser.id, {
          ...currentUser,
          sites: currentSites,
        });
      }

      setCurrentUser(INIT_USER);
      refresh();
      close();
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setCurrentUser((s) => ({
      ...s,
      [name]: value,
    }));
  };

  const handleChangeU = (event) => {
    const { name, value } = event.target;
    setCurrentUser((s) => ({
      ...s,
      [name]: value,
    }));
    setSubmitVisible(false);
    if (userRole === "admin") setSubmitVisible(true);
  };

  const handleSitesChange = (e, sites) => {
    let sitesToAdd = [];
    sites.forEach(({ siteCode }) => sitesToAdd.push({ siteCode }));
    setCurrentSites(sitesToAdd);
    setSubmitVisible(false);
    if (sitesToAdd.length > 0) setSubmitVisible(true);
  };

  const handleChangeCheck = (e) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      setCurrentUser((s) => ({ ...s, [name]: !s[name] }));
    } else {
      setCurrentUser((s) => ({ ...s, [name]: value }));
    }
  };

  const {
    userRole,
    displayName,
    email,
    password,
    confirmPassword,
    telephone,
    position,
    showCampaigns,
  } = currentUser;

  useEffect(() => {
    getAllSites().then((sites) => setSites(sites));
  }, [apiUrl]);

  const ActivateUser = async () => {
    try {
      const res = await Axios.put(
        `${apiUrl}/api/library/activateuser/${user.id}`
      );
      if (res.data.success === true) {
        enqueueSnackbar(`User Activated`, { variant: "success" });
      }
      close();
    } catch (error) {
      enqueueSnackbar(`Couldn't activate user: ${error.message}`, {
        variant: "error",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormControl>
            <InputLabel id="userRole">User Role</InputLabel>
            <Select
              type="number"
              name="userRole"
              label="User Role"
              labelId="userRole"
              value={userRole}
              defaultValue="user"
              onChange={handleChangeU}
              required
            >
              <MenuItem value="user">Standard User</MenuItem>
              <MenuItem value="admin">Administrator</MenuItem>
              <MenuItem value="orderOnly">Order Only User</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            multiple
            name="sites"
            labelId="siteCode"
            options={sites}
            getOptionLabel={(site) => site.siteCode + "-" + site.name}
            filterSelectedOptions
            onChange={handleSitesChange}
            renderInput={(params) => (
              <TextField {...params} label="Sites" placeholder="Select sites" />
            )}
            required
          />
          <TextField
            type="tel"
            name="telephone"
            value={telephone}
            onChange={handleChange}
            label="Phone"
          />
          <TextField
            name="position"
            value={position}
            onChange={handleChange}
            label="Position"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            name="displayName"
            value={displayName}
            onChange={handleChange}
            label="Display Name"
            required
          />
          <TextField
            type="email"
            name="email"
            value={email}
            onChange={handleChange}
            label="Email"
            required
          />
          {!user && (
            <>
              <TextField
                type="password"
                name="password"
                value={password}
                onChange={handleChange}
                label="Password"
                required
              />
              <TextField
                type="password"
                name="confirmPassword"
                value={confirmPassword}
                onChange={handleChange}
                label="Confirm Password"
                required
              />
            </>
          )}

          <Checkbox
            name="showCampaigns"
            onChange={handleChangeCheck}
            checked={showCampaigns}
            label="Show Campaign"
          />
        </Grid>
      </Grid>
      <Actions>
        <Button margin="2rem" marginLeft="0%" onClick={ActivateUser}>
          Activate User
        </Button>
        <Button
          type="submit"
          colour="success"
          margin="2rem"
          disabled={!submitVisible}
        >
          {user ? "Update" : "Create"} User
        </Button>
      </Actions>
    </form>
  );
};

const Actions = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
`;

export default UserForm;
